import { loadStripe } from '@stripe/stripe-js/pure';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { BehaviorSubject, Subject } from 'rxjs';

import AddAdditionalFilesDialog from '../components/additionalFiles/AddAdditionalFilesDialog';
import ChangeStudyTitle from '../components/changeStudyTitle/ChangeStudyTitle';
import PersonalizedVideoFilesRadiologyReport from '../components/personalizedVideoFiles/PersonalizedVideoFilesRadiologyReportDialog';
import PreferredDatePopup from '../components/preferredDatePopup/PreferredDatePopup';
import { UPLOAD_OPTIONS } from '../constants/general';
import UploadVideoReportProgress from '../containers/upload/UploadVideoReportProgress';
import { caseStatuses } from '../models/Case';
import { DCM, DOCS, PDF } from '../models/Resource';
import { WorkspaceProductType } from '../models/Workspace';
import parseFiles from '../utils/dicomUtils';

const defaultStatus = {
  filesStatus: {
    existingStudy: false,
    fileType: DCM,
    files: [],
    hasReport: false,
    imageCount: 0,
    reportUploaded: false,
    resourceTitle: undefined,
    study: null,
    uploaded: false,
    uploadedReport: [],
  },
  formOnlineFolderLink: {
    onlineServiceName: '',
    sharingLink: '',
    studyDescription: '',
  },
  formOnlinePortalAccess: {
    portalAddress: '',
    portalPassword: '',
    portalUsername: '',
    studyName: '',
  },
  formStatus: {
    dateOfBirth: null,
    gender: null,
    phoneNumber: null,
    preferredName: '',
    question: '',
    reportDate: null,
    termsCheck: false,
  },
  imageUploadOption: UPLOAD_OPTIONS.UPLOAD_SELF,
  isBtnEnabled() {
    const validStudy =
      (this.filesStatus.existingStudy && this.filesStatus.reportUploaded) ||
      (this.filesStatus.existingStudy && this.filesStatus.hasReport) ||
      (this.filesStatus.uploaded && this.filesStatus.imageCount);
    const validForm = this.formStatus.termsCheck && this.formStatus.validForm;

    // uploadOption: online-portal-access
    if (this.imageUploadOption === UPLOAD_OPTIONS.ONLINE_PORTAL_ACCESS) {
      const { portalAddress, portalPassword, portalUsername, studyName } = this.formOnlinePortalAccess;
      return portalAddress && portalPassword && portalUsername && studyName && validForm;
    }

    // uploadOption: folder-share-link
    if (this.imageUploadOption === UPLOAD_OPTIONS.FOLDER_SHARE_LINK) {
      const { onlineServiceName, sharingLink, studyDescription } = this.formOnlineFolderLink || {};
      return !!onlineServiceName && !!sharingLink && !!studyDescription && validForm;
    }

    if (this.imageUploadOption === UPLOAD_OPTIONS.IMAGELESS) {
      return validForm;
    }

    // upload-self
    return validForm && validStudy && !this.isLoading;
  },

  isFileDetailsCompleted(workspace = {}) {
    if (this.imageUploadOption === UPLOAD_OPTIONS.ONLINE_PORTAL_ACCESS) {
      const { portalAddress, portalPassword, portalUsername, studyName, validForm } = this.formOnlinePortalAccess || {};
      return !!portalAddress && !!portalPassword && !!portalUsername && !!studyName && validForm && this.filesStatus.reportUploaded;
    }

    if (this.imageUploadOption === UPLOAD_OPTIONS.FOLDER_SHARE_LINK) {
      const { onlineServiceName, sharingLink, studyDescription, validForm } = this.formOnlineFolderLink || {};

      return !!onlineServiceName && !!sharingLink && !!studyDescription && validForm && this.filesStatus.reportUploaded;
    }

    if (this.imageUploadOption === UPLOAD_OPTIONS.USE_EXISTING_STUDY) {
      if (workspace?.radiologyReportRequired) {
        if (this.filesStatus.existingStudy && this.filesStatus.hasReport) return true;
        return this.filesStatus.existingStudy && this.filesStatus.reportUploaded;
      }

      return this.filesStatus.existingStudy;
    }

    if (!workspace?.radiologyReportRequired) {
      return this.filesStatus.uploaded && this.filesStatus.imageCount;
    }

    return (this.filesStatus.existingStudy && this.filesStatus.hasReport) || (this.filesStatus.uploaded && this.filesStatus.imageCount && this.filesStatus.reportUploaded);
  },
  isLoading: false,
  isPersonalDetailsCompleted() {
    return !!this.formStatus.dateOfBirth && !!this.formStatus.gender && !!this.formStatus.phoneNumber && !!this.formStatus.preferredName;
  },
};

const REPORT_FORMATS = [PDF, 'jpg', 'jpeg', 'tiff', 'png', 'txt', 'rtf', 'doc', 'docx'];

export default class PersonalizedVideoReportBloc {
  status$ = new BehaviorSubject(defaultStatus);
  uploadResult$ = new Subject();
  paymentResult$ = new Subject();
  isSubmitting$ = new BehaviorSubject(false);
  studies$ = new BehaviorSubject([]);
  selectedStudyId$ = new BehaviorSubject(null);
  submitButtonError$ = new BehaviorSubject(null);
  shouldRedirectToDashboard$ = new BehaviorSubject(false);
  couponData$ = new BehaviorSubject({});

  constructor({
    dialogRepository,
    paymentRepository,
    casesRepository,
    authRepository,
    patientRepository,
    userRepository,
    errorRepository,
    uploadProgressRepository,
    workspaceRepository,
    freshchatService,
  }) {
    this.dialogRepository = dialogRepository;
    this.paymentRepository = paymentRepository;
    this.casesRepository = casesRepository;
    this.authRepository = authRepository;
    this.patientRepository = patientRepository;
    this.userRepository = userRepository;
    this.errorRepository = errorRepository;
    this.uploadProgressRepository = uploadProgressRepository;
    this.workspaceRepository = workspaceRepository;
    this.freshchatService = freshchatService;

    this.handleGetWorkspaceDiscountDetail();
    this.authRepository.getWorkspace();
  }

  get userWorkspace$() {
    return this.authRepository.userWorkspace$;
  }

  async initForm() {
    const user = this.userRepository.userDetails$.getValue();
    this.updateFormStatus(user.gender, 'gender');
    this.updateFormStatus(user.phoneNumber, 'phoneNumber');
    this.updateFormStatus(user.dateOfBirth, 'dateOfBirth');
    this.updateFormStatus(user.preferredName, 'preferredName');

    this.checkIsMediphanyWorkspace();
  }

  async initStudiesData(studyId) {
    const studies = await this.patientRepository.getPatientStudies();
    this.studies$.next(studies || []);
    if (studies?.length) {
      this.updateImageUploadOption(UPLOAD_OPTIONS.USE_EXISTING_STUDY);
      this.selectedStudyId$.next(studyId || studies[0]?.id);
      this.populateStudy(studyId || studies[0]?.id);
    }
  }

  async initStripe() {
    if (!this.stripe) {
      this.stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISH_KEY, {
        apiVersion: '2020-08-27',
      });
    }
  }

  openUploadReportDialog() {
    this.dialogRepository.loadDialog(PersonalizedVideoFilesRadiologyReport, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onReportUpload: (files) => {
        this.uploadReports(files);
        this.dialogRepository.toggleDialog(false);
      },
    });
  }

  async parseFiles(files) {
    try {
      const filesStatus = await parseFiles(files, { ...this.status$.getValue().filesStatus });
      if (filesStatus) {
        this.status$.next({
          ...this.status$.getValue(),
          filesStatus: {
            ...filesStatus,
            fileType: DCM,
            hasReport: false,
          },
        });
      }
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message,
        severity: 'error',
      });
    }
  }

  checkIsMediphanyWorkspace() {
    const user = this.userRepository.userDetails$.getValue();
    if (user?.workspace?.isMediphanyWorkspace) return;
    if (user?.workspace?.productType === WorkspaceProductType.whiteGlove.value) return;

    this.shouldRedirectToDashboard$.next(true);
  }

  setFormStatus(formStatus) {
    this.status$.next({
      ...this.status$.getValue(),
      formStatus,
    });
  }

  setFormOnlinePortalAccess(formOnlinePortalAccess) {
    this.status$.next({
      ...this.status$.getValue(),
      formOnlinePortalAccess,
    });
  }

  setFormOnlineFolderLink(formOnlineFolderLink) {
    this.status$.next({
      ...this.status$.getValue(),
      formOnlineFolderLink,
    });
  }

  async handleGetWorkspaceDiscountDetail() {
    const result = await this.workspaceRepository.discountDetail();
    this.couponData$.next(result?.discount);
  }

  resetData() {
    this.status$.next({ ...defaultStatus });
  }

  removeReport() {
    const currentStatus = this.status$.getValue();
    this.status$.next({
      ...currentStatus,
      filesStatus: {
        ...currentStatus.filesStatus,
        reportUploaded: false,
        uploadedReport: [],
      },
    });
  }

  removeFiles() {
    const currentStatus = this.status$.getValue();
    this.status$.next({
      ...currentStatus,
      filesStatus: {
        ...currentStatus.filesStatus,
        files: [],
        hasReport: false,
        imageCount: 0,
        uploaded: false,
      },
    });
  }

  updateFormStatus(value, field, validForm = true) {
    const updatedStatus = {
      ...this.status$.getValue().formStatus,
      [field]: value,
      validForm,
    };
    this.setFormStatus(updatedStatus);
  }

  updateFormOnlinePortalAccess(value, field, validForm = true) {
    const formOnlinePortalAccess = {
      ...this.status$.getValue().formOnlinePortalAccess,
      [field]: value,
      validForm,
    };

    this.setFormOnlinePortalAccess(formOnlinePortalAccess);
  }

  updateOnlineFolderLink(value, field, validForm = true) {
    const formOnlineFolderLink = {
      ...this.status$.getValue().formOnlineFolderLink,
      [field]: value,
      validForm,
    };

    this.setFormOnlineFolderLink(formOnlineFolderLink);
  }

  updateImageUploadOption(value) {
    this.status$.next({
      ...this.status$.getValue(),
      imageUploadOption: value,
    });
  }

  setStudy(study) {
    this.status$.next({
      ...this.status$.getValue(),
      filesStatus: {
        ...this.status$.getValue().filesStatus,
        study,
      },
    });
  }

  openChangeTitle() {
    this.dialogRepository.loadDialog(ChangeStudyTitle, {
      onCancel: () => this.dialogRepository.toggleDialog(false),
      onSave: (value) => {
        this.setStudy(value);
        this.dialogRepository.toggleDialog(false);
      },
      study: this.status$.getValue().filesStatus.study,
    });
  }

  openPreferredDate() {
    this.dialogRepository.loadDialog(PreferredDatePopup, {
      onDismiss: () => this.dialogRepository.toggleDialog(false),
    });
  }

  uploadReports(files) {
    const fileArray = Array.from(files);
    const reports = [];

    for (let idx = 0; idx < fileArray.length; idx += 1) {
      const file = fileArray[idx];

      if (file) {
        const parts = file.name.split('.');
        const extension = parts[parts.length - 1];
        if (!REPORT_FORMATS.includes(extension.toLowerCase())) {
          return this.errorRepository.error$.next({
            message: 'Uploaded report must be a pdf/doc/txt/image file',
            severity: 'error',
          });
        }
        reports.push(file);
      }
    }

    if (reports.length) {
      const fileStatus = this.status$.getValue().filesStatus;

      return this.status$.next({
        ...this.status$.getValue(),
        filesStatus: {
          ...fileStatus,
          reportUploaded: true,
          uploadedReport: reports,
        },
      });
    }

    return null;
  }

  async openImageSelectionDialog() {
    this.dialogRepository.loadDialog(AddAdditionalFilesDialog, {
      dialogTitle: 'Select Files',
      onCrossIconClick: () => this.closeDialog(),
      onUpload: (data) => {
        this.closeDialog();
        if (data.type === DCM) {
          this.parseFiles(data.files);
          return;
        }

        const fileType = data.type === DOCS ? PDF : data.type;

        const status = this.status$.getValue();
        this.status$.next({
          ...status,
          filesStatus: {
            ...status.filesStatus,
            fileType,
            files: data.files || [],
            hasReport: false,
            imageCount: 1,
            study: data.files[0].name || '',
            uploaded: true,
          },
        });
      },
    });
  }

  closeDialog() {
    this.dialogRepository.toggleDialog(false);
  }

  async populateStudy(id) {
    try {
      this.setLoading(true);
      const fileStatus = this.status$.getValue().filesStatus;
      const record = await this.patientRepository.getRecord(id);
      this.status$.next({
        ...this.status$.getValue(),
        filesStatus: {
          ...fileStatus,
          existingStudy: id,
          hasReport: record.case?.reports?.length,
          imageCount: record.imageCount,
          resourceTitle: record?.title,
          study: record?.description,
          uploaded: true,
        },
      });

      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      throw error;
    }
  }

  getDateOfBirth() {
    const { formStatus } = this.status$.getValue();
    if (!formStatus.dateOfBirth) return null;

    if (typeof formStatus.dateOfBirth === 'string') {
      return formStatus.dateOfBirth;
    }

    const dateMoment = moment(formStatus.dateOfBirth);
    return dateMoment.format('YYYY-MM-DD');
  }

  async sendCase() {
    try {
      const { filesStatus, formStatus } = this.status$.getValue();

      await this.userRepository.getCurrentUser();

      const user = this.userRepository.userDetails$.getValue();
      const workspace = this.userWorkspace$.getValue();

      const { imageUploadOption } = this.status$.getValue();
      let studyId = filesStatus?.existingStudy || null;
      if (imageUploadOption !== UPLOAD_OPTIONS.USE_EXISTING_STUDY) studyId = null;

      const payload = {
        files: filesStatus?.files,
        mainFileType: filesStatus?.fileType || DCM,
        patient: {
          dateOfBirth: this.getDateOfBirth(),
          gender: formStatus.gender,
          phoneNumber: formStatus.phoneNumber,
          preferredName: formStatus.preferredName || null,
          userId: this.authRepository.auth$.getValue().id,
          workspaceId: this.authRepository.auth$.getValue().workspaceId,
        },
        preferredName: formStatus.preferredName || null,
        preferredReportDate: formStatus.reportDate ? formStatus.reportDate.toISOString() : null,
        questionText: formStatus.question,
        resourceTitle: studyId ? filesStatus?.resourceTitle : null,
        studyDescription: filesStatus?.study,
        studyId,
        uploadedReport: filesStatus?.uploadedReport,
        userReportId: user.availableReport?.id || undefined,
      };

      if (workspace?.radiologyReportRequired && !filesStatus?.reportUploaded && !filesStatus.hasReport) {
        this.submitButtonError$.next('Please upload your radiology report to continue.');
        return;
      }

      this.isSubmitting$.next(true);
      this.uploadProgressRepository.start();

      const uploadWithoutFile = ['pending-upload'].includes(imageUploadOption);
      this.dialogRepository.loadDialog(UploadVideoReportProgress, { preventClose: true, shouldRemoveFileUploadDetail: uploadWithoutFile });

      let result = {};
      if (imageUploadOption === UPLOAD_OPTIONS.ONLINE_PORTAL_ACCESS) {
        result = await this.requestUploadSharedReport();
      } else if (imageUploadOption === UPLOAD_OPTIONS.FOLDER_SHARE_LINK) {
        result = await this.requestUploadSharedLinkReport();
      } else if (imageUploadOption === UPLOAD_OPTIONS.IMAGELESS) {
        payload.dcmRequestType = 'UserRequestedImagelessVideoReport';
        result = await this.casesRepository.requestVideoReport(payload, this.uploadProgressRepository.progress$);
      } else result = await this.casesRepository.requestVideoReport(payload, this.uploadProgressRepository.progress$);

      if (result?.status === caseStatuses['not-paid'].value) {
        this.uploadProgressRepository.finish();
        this.dialogRepository.toggleDialog(false);
        this.paymentResult$.next(result?.dicomStudy?.id);
      } else {
        this.uploadResult$.next(true);
      }

      if (!filesStatus.existingStudy) {
        this.errorRepository.error$.next({
          message: 'Your files are being processed. Once this is completed you will be notified by email.',
          severity: 'success',
        });
      }

      this.uploadProgressRepository.finish();
      await this.userRepository.getCurrentUser();
      this.userRepository.removeIsPatientDemoAccount();
      this.userRepository.removeIsPatientDashboardTourShown();
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message,
        severity: 'error',
      });
      this.uploadProgressRepository.fail();
    } finally {
      this.isSubmitting$.next(false);
    }
  }

  async requestUploadSharedReport() {
    const { formStatus, formOnlinePortalAccess, imageUploadOption, filesStatus } = this.status$.getValue();
    if (imageUploadOption !== UPLOAD_OPTIONS.ONLINE_PORTAL_ACCESS) return null;

    const user = this.userRepository.userDetails$.getValue();

    const payload = {
      patient: {
        dateOfBirth: this.getDateOfBirth(),
        gender: formStatus.gender,
        phoneNumber: formStatus.phoneNumber,
        preferredName: formStatus.preferredName || null,
        userId: this.authRepository.auth$.getValue().id,
        workspaceId: this.authRepository.auth$.getValue().workspaceId,
      },
      preferredName: formStatus.preferredName || null,
      preferredReportDate: formStatus.reportDate ? formStatus.reportDate.toISOString() : null,
      questionText: formStatus.question,
      uploadSharedInfo: {
        portalAddress: formOnlinePortalAccess.portalAddress,
        portalPassword: formOnlinePortalAccess.portalPassword,
        portalUsername: formOnlinePortalAccess.portalUsername,
        studyName: formOnlinePortalAccess.studyName,
      },
      uploadedReport: filesStatus?.uploadedReport || [],
      userReportId: user.availableReport?.id || undefined,
    };

    return this.casesRepository.requestUploadSharedReport(payload, this.uploadProgressRepository.progress$);
  }

  async requestUploadSharedLinkReport() {
    const { filesStatus, formStatus, formOnlineFolderLink } = this.status$.getValue();

    const user = this.userRepository.userDetails$.getValue();

    const payload = {
      caseLinkInfo: {
        onlineServiceName: formOnlineFolderLink.onlineServiceName,
        sharingLink: formOnlineFolderLink.sharingLink,
        studyDescription: formOnlineFolderLink.studyDescription,
      },
      patient: {
        dateOfBirth: this.getDateOfBirth(),
        gender: formStatus.gender,
        phoneNumber: formStatus.phoneNumber,
        preferredName: formStatus.preferredName || null,
        userId: this.authRepository.auth$.getValue().id,
        workspaceId: this.authRepository.auth$.getValue().workspaceId,
      },
      preferredName: formStatus.preferredName || null,
      preferredReportDate: formStatus.reportDate ? formStatus.reportDate.toISOString() : null,
      questionText: formStatus.question,
      uploadedReport: filesStatus?.uploadedReport || [],
      userReportId: user.availableReport?.id || undefined,
    };

    return this.casesRepository.requestUploadSharedLinkReport(payload, this.uploadProgressRepository.progress$);
  }

  async onSelectStudy(studyId) {
    this.selectedStudyId$.next(studyId);
    if (!studyId) return;
    await this.populateStudy(studyId);
  }

  async onFilesUpload(files) {
    await this.parseFiles(files);
  }

  openFreshChatDialog() {
    this.freshchatService.openDialog();
  }

  setLoading(state) {
    this.status$.next({
      ...this.status$.getValue(),
      isLoading: state,
    });
  }

  setSubmitButtonError(content) {
    this.submitButtonError$.next(content);
  }

  manageSubmitButtonError() {
    const { filesStatus } = this.status$.getValue();

    if (!filesStatus?.reportUploaded && !filesStatus.hasReport) return;

    this.submitButtonError$.next('');
  }
}
